import {ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {ButtonGroup, IconButton} from '@primer/react'
import {useCallback, useEffect, useRef} from 'react'
import {useDiffNavigation} from '../../hooks/use-diff-navigation'
import {useSelectedPathDigest} from '../../stores/selectors'
import {SingleFileModeOnboardingPopover} from './SingleFileModeOnboardingPopover'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import type {FilesPageNotices, UserNotice} from '../../page-data/payloads/files'

type ReviewNavigationButtonGroupProps = {
  userNotices?: Array<UserNotice<FilesPageNotices>>
}

export function ReviewNavigationButtonGroup({userNotices}: ReviewNavigationButtonGroupProps) {
  const areShortcutsEnabled = areCharacterKeyShortcutsEnabled()
  const {navigateToPrevious, navigateToNext, prefetchPrevious, prefetchNext} = useDiffNavigation()
  const selectedPathDigest = useSelectedPathDigest()
  const hoveredButtonRef = useRef<'previous' | 'next' | null>(null)

  // When the selected path changes while a button is hovered, re-prefetch
  // for the new adjacent file (e.g., user presses j/k while hovering)
  useEffect(() => {
    if (hoveredButtonRef.current === 'previous') prefetchPrevious()
    else if (hoveredButtonRef.current === 'next') prefetchNext()
  }, [selectedPathDigest, prefetchPrevious, prefetchNext])

  const onPreviousEnter = useCallback(() => {
    hoveredButtonRef.current = 'previous'
    prefetchPrevious()
  }, [prefetchPrevious])

  const onNextEnter = useCallback(() => {
    hoveredButtonRef.current = 'next'
    prefetchNext()
  }, [prefetchNext])

  const onPointerLeave = useCallback(() => {
    hoveredButtonRef.current = null
  }, [])

  const previousLabel = areShortcutsEnabled ? 'Previous file (K)' : 'Previous file'
  const nextLabel = areShortcutsEnabled ? 'Next file (J)' : 'Next file'
  return (
    <div className="position-relative">
      <ButtonGroup>
        <IconButton
          icon={ChevronUpIcon}
          aria-label={previousLabel}
          size="small"
          onClick={navigateToPrevious}
          onPointerEnter={onPreviousEnter}
          onPointerLeave={onPointerLeave}
          data-hotkey="k"
        />
        <IconButton
          icon={ChevronDownIcon}
          aria-label={nextLabel}
          size="small"
          onClick={navigateToNext}
          onPointerEnter={onNextEnter}
          onPointerLeave={onPointerLeave}
          data-hotkey="j"
        />
      </ButtonGroup>
      <SingleFileModeOnboardingPopover userNotices={userNotices} />
    </div>
  )
}
